import React from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';

import { ROUTESLIST } from '@/static/urls';
import FormField from '@/components/forms/form-field';
import { arrangeData } from '@/middlewares/api';
import GoogleMapsAutocompleteField from '@/components/forms/google-maps-autocomplete';
import { withFormikFieldAdapter as formikFieldAdapter } from '@/components/forms/formik-adapter';
import OneAutocompleteField from "@/components/forms/one-autocomplete";
import {autocompleteString} from "@/utils/forms";

const validationSchema = Yup.object().shape({
    name: Yup.string().max(250, 'Contact Name must be at most 250 characters').required('Contact Name is required'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    your_company: Yup.string().nullable(),
    distribution: autocompleteString(Yup, 'Company distributing your project is required'),
    business_address: Yup.string().max(250, 'Business address must be at most 250 characters').required('Business address is required'),
    phone_number: Yup.string()
        .matches(/^([0-9\s\-+()]*)$/, 'Invalid phone number format')
        .min(10, 'Phone number must be at least 10 characters')
        .required('Phone number is required'),
    party_responsible_payment: Yup.string().max(250, 'Responsible party for payment must be at most 250 characters').required('Responsible party for payment is required'),
    author_sign_sample_agreement: Yup.string().max(250, 'Signee for sample agreement must be at most 250 characters').required('Signee for sample agreement is required'),
    party_payment_email: Yup.string().email('Invalid email address').required('Payment email is required'),
});

const FormFieldFormik = formikFieldAdapter(FormField);
const GoogleMapsAutocompleteFieldFormik = formikFieldAdapter(GoogleMapsAutocompleteField);
const OneAutocompleteFieldFormik = formikFieldAdapter(OneAutocompleteField);

const UploadSyncStep1 = ({ nextStep, initialValues, bgImg }) => {
    return (
        <section className="upload-form__sec" style={bgImg}>
            <div className="content">
                <div className="upload-form__top">
                    <h2 className="head-shadow">CLEAR SYNC</h2>
                    <h3 className="head">
                        Step 1 of 4: CLIENT INFO
                    </h3>
                </div>
                <div className="form-step__body">
                    <Formik
                        initialValues={{
                            name: '',
                            email: '',
                            your_company: '',
                            distribution: '',
                            business_address: '',
                            phone_number: '',
                            party_responsible_payment: '',
                            author_sign_sample_agreement: '',
                            party_payment_email: '',
                            ...initialValues
                        }}
                        validationSchema={validationSchema}
                        enableReinitialize={true}
                        onSubmit={(values) => {
                            const formData = arrangeData(values);
                            nextStep(formData, values);
                        }}
                    >
                        {({ isSubmitting }) => (
                            <Form className="form-layout" noValidate>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="name"
                                            component={FormFieldFormik}
                                            required={true}
                                            type="text"
                                            placeholder="Set First and Last Name"
                                            label="Contact Name"
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="email"
                                            component={FormFieldFormik}
                                            required={true}
                                            type="email"
                                            placeholder="some@email.com"
                                            label="Your email address"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="your_company"
                                            component={FormFieldFormik}
                                            required={false}
                                            type="text"
                                            placeholder="Name of your company"
                                            label="Name of your company"
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="distribution"
                                            component={OneAutocompleteFieldFormik}
                                            url="clear/distributor/search"
                                            required={true}
                                            type="text"
                                            placeholder="Company distributing your project"
                                            label="Company distributing your project"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="business_address"
                                            component={GoogleMapsAutocompleteFieldFormik}
                                            required={true}
                                            type="text"
                                            placeholder="Your Business Address"
                                            label="Your Business Address"
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="phone_number"
                                            component={FormFieldFormik}
                                            required={true}
                                            type="text"
                                            placeholder="+1(999) 999-9999"
                                            label="Your Phone Number"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="party_responsible_payment"
                                            component={FormFieldFormik}
                                            required={true}
                                            type="text"
                                            placeholder="Party Responsible for Making Payment?"
                                            label="Party Responsible for Making Payment"
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="author_sign_sample_agreement"
                                            component={FormFieldFormik}
                                            required={true}
                                            type="text"
                                            placeholder="Set First and Last Name"
                                            label="Who will sign Sample Agreement?"
                                        />
                                    </div>
                                </div>
                                <Field
                                    name="party_payment_email"
                                    component={FormFieldFormik}
                                    required={true}
                                    type="email"
                                    placeholder="some@email.com"
                                    label="Party Responsible for Making Payment Email Address"
                                />
                                <div className="submit-row_txt">
                                    <p className="as-text">
                                        By clicking Next button you are agreeing to the
                                        <Link to={ROUTESLIST.STATIC_TERMS} className="link-thin"
                                            target="_blank" rel="noopener noreferrer"
                                        >Terms and Conditions.
                                        </Link>
                                    </p>
                                    <div className="submit-row__btn-col">
                                        <button
                                            className="btn-red-primary"
                                            disabled={isSubmitting}
                                            type="submit">Next</button>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </section>
    );
};

export default UploadSyncStep1;
